import React from 'react';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { cn, getAnchorId } from '@/helpers/utils';
import Divider from '../common/Divider';

interface FAQ {
  title: string;
  body: string;
}

interface FAQsProps {
  faqs: FAQ[];
  sectionHeader: string;
  showMobileView?: boolean;
}

const FAQs: React.FC<FAQsProps> = ({
  faqs,
  sectionHeader,
  showMobileView = false,
}) => {
  const faqBodyRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  const { customTrack, identifier } = useAnalyticsContext();

  React.useEffect(() => {
    const cleanups: Array<() => void> = [];

    faqBodyRefs.current.forEach((container, idx) => {
      if (!container) return;

      const faq = faqs[idx];
      if (!faq) return;

      const handleLinkClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        const anchor = target?.closest('a') as HTMLAnchorElement | null;
        if (!anchor?.href) return;
        if (!container.contains(anchor)) return;

        event.preventDefault();

        const linkText = (anchor.textContent || '').trim() || anchor.href;

        customTrack({
          event,
          identifier,
          element: 'Frequently Asked Questions',
          detail: `${faq.title}>>${linkText}`,
        });

        const openedWindow = window.open(
          anchor.href,
          '_blank',
          'noopener,noreferrer'
        );
        if (openedWindow) {
          openedWindow.opener = null;
        }
      };

      container.addEventListener('click', handleLinkClick);
      cleanups.push(() =>
        container.removeEventListener('click', handleLinkClick)
      );
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [faqs, customTrack, identifier]);

  return (
    <div className="flex tw-justify-center">
      <div className="w-full tw-max-w-xl">
        <h2 className="margin-3-b color-text-emphasis tw-font-sans tw-text-2xl">
          {sectionHeader}
        </h2>
        {faqs.map((faq, idx) => {
          const anchorInput = {
            type: 'faq',
            name: undefined,
            title: faq.title,
            idx,
            section: sectionHeader,
          };

          return (
            <div
              key={faq.title}
              id={getAnchorId(anchorInput)}
              className={idx !== 0 ? 'tw-mt-12' : 'tw-mt-4'}
            >
              <div
                className={cn('tw-text-md tw-mb-2 tw-font-sans tw-font-bold', {
                  'md_tw-text-lg': !showMobileView,
                })}
              >
                {faq.title}
              </div>
              <div
                ref={(node) => {
                  faqBodyRefs.current[idx] = node;
                }}
                className={cn(
                  'tw-mb-4 tw-text-sm a-children_tw-text-blue-bright-600 a-children_tw-underline',
                  { 'md_tw-text-md': !showMobileView }
                )}
                dangerouslySetInnerHTML={{ __html: faq.body }}
              />
              {idx < faqs.length - 1 && <Divider />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FAQs;
